import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '../../common';
import { EdiService, formatCents, formatEdiDate } from '../../../services/billing/edi.service';
import type { EdiDeadlineClaim } from '../../../dtos/billing/edi.dto';
import { ErrorNote, deadlineTone, errorText } from './ediUi';

/** TRK-03 deadline board: claims not paid, sorted by days left (≤5 red, ≤15 amber). */
const DeadlineBoard: React.FC = () => {
  const [claims, setClaims] = useState<EdiDeadlineClaim[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    EdiService.listDeadlines()
      .then((c) => { if (!cancelled) setClaims(c); })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load filing deadlines')); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-3 p-3 sm:p-4" aria-label="Deadline board">
      <h2 className="text-base font-semibold text-slate-800">Filing deadlines</h2>
      <ErrorNote message={error} />
      {!claims && !error && <LoadingSpinner label="Loading deadlines" />}
      {claims && claims.length === 0 && <p className="text-sm text-slate-500">No unpaid claims near their filing deadline.</p>}
      {claims && claims.length > 0 && (
        <ul className="space-y-2">
          {claims.map((c) => (
            <li key={c.claimId} className={`rounded-md border p-2 text-sm ${deadlineTone(c.daysLeft)}`} data-testid={`deadline-${c.claimId}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{c.patientName} · {c.payerName}</span>
                <span className="font-semibold">{c.daysLeft < 0 ? `${Math.abs(c.daysLeft)} days past` : `${c.daysLeft} ${c.daysLeft === 1 ? 'day' : 'days'} left`}</span>
              </div>
              <p className="text-xs">Deadline {formatEdiDate(c.filingDeadline)} · {c.status} · {formatCents(c.amountCents)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DeadlineBoard;
